import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  BarChart3, Calendar, Trophy, TrendingUp, TrendingDown, Users,
  Loader2, ChevronLeft, ChevronRight, RefreshCw, Settings, FileText,
  Save, RotateCcw, Send, Eye, PartyPopper, Medal, EyeOff
} from 'lucide-react';

const getMonthName = (month) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return months[month - 1];
};

const AdminSummaries = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [summaries, setSummaries] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [motivationalMessage, setMotivationalMessage] = useState('');
  const [isDefaultMessage, setIsDefaultMessage] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  
  // Month-end celebration state
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState('');
  const [celebrationPreview, setCelebrationPreview] = useState(null);
  const [sendingCelebration, setSendingCelebration] = useState(false);
  const [celebrationHistory, setCelebrationHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, [year, month]);

  useEffect(() => {
    fetchSettings();
    fetchCelebrationSettings();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summariesRes, aggregateRes] = await Promise.all([
        apiClient.get(`/admin/summaries/${year}/${month}`),
        apiClient.get(`/admin/summaries/${year}/${month}/aggregate`)
      ]);
      setSummaries(summariesRes.data);
      setAggregate(aggregateRes.data);
    } catch (err) {
      console.error('Failed to fetch summaries:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await apiClient.get('/admin/summaries/settings');
      setMotivationalMessage(res.data.motivational_message);
      setIsDefaultMessage(res.data.is_default);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const fetchCelebrationSettings = async () => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        apiClient.get('/admin/monthend/settings'),
        apiClient.get('/admin/monthend/history')
      ]);
      setCelebrationMessage(settingsRes.data.message_template);
      setCelebrationHistory(historyRes.data);
    } catch (err) {
      console.error('Failed to fetch celebration settings:', err);
    }
  };

  const previewCelebration = async () => {
    try {
      const res = await apiClient.post(`/admin/monthend/preview/${year}/${month}`);
      setCelebrationPreview(res.data);
    } catch (err) {
      toast.error('Failed to generate preview');
    }
  };

  const sendCelebration = async () => {
    if (!window.confirm(`This will send the month-end celebration message to ALL users for ${getMonthName(month)} ${year}. Continue?`)) {
      return;
    }
    
    setSendingCelebration(true);
    try {
      const res = await apiClient.post(`/admin/monthend/send/${year}/${month}`);
      if (res.data.message === "Already sent") {
        toast.info(`Month-end celebration was already sent for ${getMonthName(month)} ${year}`);
      } else {
        toast.success(res.data.message);
      }
      fetchCelebrationSettings();
      setCelebrationOpen(false);
    } catch (err) {
      toast.error('Failed to send celebration');
    } finally {
      setSendingCelebration(false);
    }
  };

  const saveCelebrationMessage = async () => {
    setSavingSettings(true);
    try {
      await apiClient.put('/admin/monthend/settings', {
        message_template: celebrationMessage
      });
      toast.success('Celebration message saved!');
    } catch (err) {
      toast.error('Failed to save message');
    } finally {
      setSavingSettings(false);
    }
  };

  const resetCelebrationMessage = async () => {
    setSavingSettings(true);
    try {
      const res = await apiClient.post('/admin/monthend/reset-message');
      setCelebrationMessage(res.data.message_template);
      toast.success('Reset to default message');
    } catch (err) {
      toast.error('Failed to reset message');
    } finally {
      setSavingSettings(false);
    }
  };

  const generateSummaries = async () => {
    setGenerating(true);
    try {
      await apiClient.post(`/admin/summaries/generate/${year}/${month}`);
      toast.success('Summary generation started! Refresh in a moment to see results.');
      setTimeout(fetchData, 3000);
    } catch (err) {
      toast.error('Failed to generate summaries');
    } finally {
      setGenerating(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await apiClient.put('/admin/summaries/settings', {
        motivational_message: motivationalMessage
      });
      toast.success('Settings saved!');
      setIsDefaultMessage(false);
      setSettingsOpen(false);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const resetMessage = async () => {
    setSavingSettings(true);
    try {
      const res = await apiClient.post('/admin/summaries/reset-message');
      setMotivationalMessage(res.data.motivational_message);
      setIsDefaultMessage(true);
      toast.success('Reset to default message');
    } catch (err) {
      toast.error('Failed to reset message');
    } finally {
      setSavingSettings(false);
    }
  };

  const changeMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setMonth(newMonth);
    setYear(newYear);
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 80) return 'text-green-400';
    if (percentage >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  const viewUserSummary = (summary) => {
    setSelectedUser(summary);
    setUserModalOpen(true);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#EFB342] rounded-2xl flex items-center justify-center">
              <BarChart3 className="text-black" size={28} />
            </div>
            <div>
              <h1 className="font-heading text-3xl text-stone-900">Monthly Summaries</h1>
              <p className="text-stone-600">View and manage user progress summaries</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setSettingsOpen(true)}
              className="border-stone-300 text-stone-700"
              data-testid="summary-settings-btn"
            >
              <Settings size={18} className="mr-2" />
              Settings
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setCelebrationOpen(true);
                previewCelebration();
              }}
              className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
              data-testid="celebration-btn"
            >
              <PartyPopper size={18} className="mr-2" />
              Month-End Celebration
            </Button>
            <Button
              onClick={generateSummaries}
              disabled={generating}
              className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
              data-testid="generate-summaries-btn"
            >
              {generating ? (
                <><Loader2 size={18} className="mr-2 animate-spin" /> Generating...</>
              ) : (
                <><RefreshCw size={18} className="mr-2" /> Generate Summaries</>
              )}
            </Button>
          </div>
        </div>

        {/* Month Selector */}
        <div className="flex items-center justify-center gap-4 mb-8 bg-white rounded-2xl p-4">
          <Button
            variant="ghost"
            onClick={() => changeMonth(-1)}
            className="text-stone-600 hover:text-stone-900"
          >
            <ChevronLeft size={24} />
          </Button>
          <h2 className="text-2xl font-semibold text-stone-900 min-w-[200px] text-center">
            {getMonthName(month)} {year}
          </h2>
          <Button
            variant="ghost"
            onClick={() => changeMonth(1)}
            className="text-stone-600 hover:text-stone-900"
          >
            <ChevronRight size={24} />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-[#EFB342]" size={40} />
          </div>
        ) : (
          <>
            {/* Aggregate Stats */}
            {aggregate && aggregate.user_count > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-stone-900 mb-4">Overall Performance</h3>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                    <Users className="mx-auto text-blue-400 mb-2" size={24} />
                    <div className="text-2xl font-bold text-stone-900">{aggregate.user_count}</div>
                    <div className="text-xs text-stone-600">Users</div>
                  </div>
                  <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                    <Trophy className="mx-auto text-[#EFB342] mb-2" size={24} />
                    <div className="text-2xl font-bold text-stone-900">{aggregate.total_points_all_users}</div>
                    <div className="text-xs text-stone-600">Total Points</div>
                  </div>
                  <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                    <TrendingUp className="mx-auto text-green-400 mb-2" size={24} />
                    <div className="text-2xl font-bold text-stone-900">{aggregate.average_points_per_user}</div>
                    <div className="text-xs text-stone-600">Avg Points/User</div>
                  </div>
                  <div className="bg-white border border-stone-200 rounded-xl p-4 text-center">
                    <Trophy className="mx-auto text-purple-400 mb-2" size={24} />
                    <div className="text-2xl font-bold text-stone-900">{aggregate.total_badges_earned}</div>
                    <div className="text-xs text-stone-600">Badges Earned</div>
                  </div>
                </div>

                {/* Action Performance */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                      <TrendingUp size={18} />
                      Strongest Actions (All Users)
                    </h4>
                    <ul className="space-y-2">
                      {aggregate.strongest_actions?.map((action, i) => (
                        <li key={i} className="text-stone-700 text-sm flex items-center gap-2">
                          <span className="text-green-400">#{i + 1}</span> {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <h4 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                      <TrendingDown size={18} />
                      Actions Needing Focus (All Users)
                    </h4>
                    <ul className="space-y-2">
                      {aggregate.weakest_actions?.map((action, i) => (
                        <li key={i} className="text-stone-700 text-sm flex items-center gap-2">
                          <span className="text-red-400">•</span> {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Individual Summaries */}
            <div>
              <h3 className="text-lg font-semibold text-stone-900 mb-4">
                Individual User Summaries ({summaries.length})
              </h3>
              {summaries.length > 0 ? (
                <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-stone-200">
                        <th className="text-left p-4 text-stone-600 font-medium">User</th>
                        <th className="text-center p-4 text-stone-600 font-medium">Points</th>
                        <th className="text-center p-4 text-stone-600 font-medium">%</th>
                        <th className="text-center p-4 text-stone-600 font-medium">Days</th>
                        <th className="text-center p-4 text-stone-600 font-medium">Badges</th>
                        <th className="text-center p-4 text-stone-600 font-medium">Top Strength</th>
                        <th className="text-center p-4 text-stone-600 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaries.map((summary, index) => {
                        const isExcluded = summary.exclude_from_leaderboard;
                        const prevSummary = index > 0 ? summaries[index - 1] : null;
                        const showExcludedHeader = isExcluded && (!prevSummary || !prevSummary.exclude_from_leaderboard);
                        
                        return (
                          <React.Fragment key={summary.id}>
                            {showExcludedHeader && (
                              <tr className="bg-stone-50">
                                <td colSpan={7} className="p-3 text-stone-600 text-sm font-medium">
                                  <div className="flex items-center gap-2">
                                    <EyeOff size={14} className="text-stone-500" />
                                    Hidden from Public Leaderboard
                                  </div>
                                </td>
                              </tr>
                            )}
                            <tr 
                              className={`border-b border-stone-200/50 hover:bg-[#F0EBE1] transition-colors ${isExcluded ? 'opacity-60' : ''}`}
                            >
                              <td className="p-4 text-stone-900 font-medium">
                                {summary.user_name}
                                {isExcluded && (
                                  <span className="ml-2 text-xs text-amber-500 bg-amber-500/20 px-2 py-0.5 rounded">Hidden</span>
                                )}
                              </td>
                              <td className="p-4 text-center text-stone-900">
                                {summary.total_points}/{summary.max_possible_points}
                              </td>
                              <td className={`p-4 text-center font-bold ${getPerformanceColor(summary.overall_percentage)}`}>
                                {summary.overall_percentage}%
                              </td>
                              <td className="p-4 text-center text-stone-700">{summary.days_checked_in}</td>
                              <td className="p-4 text-center text-[#EFB342]">{summary.badges_earned_this_month}</td>
                              <td className="p-4 text-center text-green-400 text-sm">
                                {summary.top_strengths?.[0] || '-'}
                              </td>
                              <td className="p-4 text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => viewUserSummary(summary)}
                                  className="text-[#EFB342] hover:text-[#D99F30]"
                                >
                                  <FileText size={16} className="mr-1" /> View
                                </Button>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-2xl border border-stone-200">
                  <FileText className="mx-auto text-stone-400 mb-4" size={48} />
                  <p className="text-stone-600">
                    No summaries generated for this month yet.
                    <br />
                    Click "Generate Summaries" to create them.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Settings className="text-[#EFB342]" size={24} />
                Summary Settings
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-stone-600 mb-2 block">
                  Motivational Message
                  {isDefaultMessage && (
                    <span className="ml-2 text-xs bg-stone-200 px-2 py-0.5 rounded">Default</span>
                  )}
                </label>
                <Textarea
                  value={motivationalMessage}
                  onChange={(e) => setMotivationalMessage(e.target.value)}
                  className="bg-stone-100 border-stone-300 text-stone-900 min-h-[150px]"
                  placeholder="Enter the motivational message shown at the end of each summary..."
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={saveSettings}
                  disabled={savingSettings}
                  className="flex-1 bg-[#EFB342] text-black hover:bg-[#D99F30]"
                >
                  {savingSettings ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save className="mr-2" size={18} />}
                  Save Changes
                </Button>
                <Button
                  onClick={resetMessage}
                  disabled={savingSettings || isDefaultMessage}
                  variant="outline"
                  className="border-stone-300 text-stone-700"
                >
                  <RotateCcw size={18} className="mr-2" />
                  Reset to Default
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* User Detail Modal */}
        <Dialog open={userModalOpen} onOpenChange={setUserModalOpen}>
          <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {selectedUser?.user_name}'s Summary - {getMonthName(selectedUser?.month)} {selectedUser?.year}
              </DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-stone-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-stone-900">{selectedUser.total_points}</div>
                    <div className="text-xs text-stone-600">Points</div>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-3 text-center">
                    <div className={`text-xl font-bold ${getPerformanceColor(selectedUser.overall_percentage)}`}>
                      {selectedUser.overall_percentage}%
                    </div>
                    <div className="text-xs text-stone-600">Complete</div>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-stone-900">{selectedUser.days_checked_in}</div>
                    <div className="text-xs text-stone-600">Days</div>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-[#EFB342]">{selectedUser.badges_earned_this_month}</div>
                    <div className="text-xs text-stone-600">Badges</div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-stone-600 mb-2">Action Breakdown</h4>
                  <div className="space-y-2">
                    {selectedUser.action_summaries?.map((action) => (
                      <div key={action.action_id} className="flex items-center justify-between bg-[#F0EBE1] rounded-lg p-2">
                        <span className="text-stone-900 text-sm">{action.action_name}</span>
                        <span className={`font-medium ${getPerformanceColor(action.percentage)}`}>
                          {action.points_earned}/28 ({action.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Month-End Celebration Dialog */}
        <Dialog open={celebrationOpen} onOpenChange={setCelebrationOpen}>
          <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <PartyPopper className="text-[#EFB342]" size={24} />
                Month-End Celebration - {getMonthName(month)} {year}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Winners Preview */}
              {celebrationPreview?.winners && (
                <div className="bg-gradient-to-r from-[#EFB342]/20 to-purple-500/20 rounded-xl p-4">
                  <h4 className="text-lg font-semibold text-stone-900 mb-3 flex items-center gap-2">
                    <Trophy className="text-[#EFB342]" size={20} />
                    Top 3 Finishers
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 bg-[#FFD700]/10 rounded-lg p-3">
                      <span className="text-2xl">🥇</span>
                      <span className="text-stone-900 font-medium flex-1">{celebrationPreview.winners.first?.name}</span>
                      <span className="text-[#EFB342] font-bold">{celebrationPreview.winners.first?.points} pts</span>
                    </div>
                    <div className="flex items-center gap-3 bg-[#C0C0C0]/10 rounded-lg p-3">
                      <span className="text-2xl">🥈</span>
                      <span className="text-stone-900 font-medium flex-1">{celebrationPreview.winners.second?.name}</span>
                      <span className="text-stone-700 font-bold">{celebrationPreview.winners.second?.points} pts</span>
                    </div>
                    <div className="flex items-center gap-3 bg-[#CD7F32]/10 rounded-lg p-3">
                      <span className="text-2xl">🥉</span>
                      <span className="text-stone-900 font-medium flex-1">{celebrationPreview.winners.third?.name}</span>
                      <span className="text-orange-400 font-bold">{celebrationPreview.winners.third?.points} pts</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Message Template */}
              <div>
                <label className="text-sm text-stone-600 mb-2 block flex items-center justify-between">
                  <span>Celebration Message Template</span>
                  <span className="text-xs text-stone-500">
                    Placeholders: {'{month_name}'}, {'{first_name}'}, {'{first_points}'}, etc.
                  </span>
                </label>
                <Textarea
                  value={celebrationMessage}
                  onChange={(e) => setCelebrationMessage(e.target.value)}
                  className="bg-stone-100 border-stone-300 text-stone-900 min-h-[200px] font-mono text-sm"
                  placeholder="Enter celebration message..."
                />
                <div className="flex gap-2 mt-2">
                  <Button
                    onClick={saveCelebrationMessage}
                    disabled={savingSettings}
                    size="sm"
                    className="bg-stone-200 hover:bg-stone-300"
                  >
                    <Save size={14} className="mr-1" /> Save Template
                  </Button>
                  <Button
                    onClick={resetCelebrationMessage}
                    disabled={savingSettings}
                    size="sm"
                    variant="outline"
                    className="border-stone-300"
                  >
                    <RotateCcw size={14} className="mr-1" /> Reset to Default
                  </Button>
                </div>
              </div>

              {/* Message Preview */}
              {celebrationPreview?.message && (
                <div>
                  <label className="text-sm text-stone-600 mb-2 block flex items-center gap-2">
                    <Eye size={14} /> Message Preview
                  </label>
                  <div className="bg-stone-50 border border-stone-300 rounded-xl p-4 whitespace-pre-line text-stone-700 text-sm max-h-[200px] overflow-y-auto">
                    {celebrationPreview.message}
                  </div>
                </div>
              )}

              {/* Celebration History */}
              {celebrationHistory.length > 0 && (
                <div>
                  <label className="text-sm text-stone-600 mb-2 block">Recent Celebrations Sent</label>
                  <div className="space-y-2">
                    {celebrationHistory.slice(0, 3).map((item, i) => (
                      <div key={i} className="flex items-center justify-between bg-[#F0EBE1] rounded-lg p-2 text-sm">
                        <span className="text-stone-900">{getMonthName(item.month)} {item.year}</span>
                        <span className="text-green-400">✓ Sent to {item.notifications_sent} users</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Check if already sent for this month */}
              {celebrationHistory.some(h => h.year === year && h.month === month) ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                  <p className="text-green-400 font-medium">
                    ✓ Month-end celebration already sent for {getMonthName(month)} {year}
                  </p>
                </div>
              ) : (
                <Button
                  onClick={sendCelebration}
                  disabled={sendingCelebration}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-stone-900 py-3"
                >
                  {sendingCelebration ? (
                    <><Loader2 size={18} className="mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Send size={18} className="mr-2" /> Send Celebration to All Users</>
                  )}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminSummaries;
